import { Card } from "@nimit9/signet-ui/components/card"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import type { CategoryDetailTransaction } from "@paisa/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { api } from "@/lib/api"
import { CATEGORY_COLORS, PERIOD_OPTIONS } from "@/lib/constants"
import { decryptTransactionRows } from "@/lib/crypto"
import { useCryptoStore } from "@/store/crypto"
import { useUIStore } from "@/store/ui"
import { BackToAnalytics, DetailSkeleton, EntityDetailBody } from "./-entity-detail"

export const Route = createFileRoute("/analytics/categories/$slug")({
  component: CategoryDetail,
})

type MonthlyTotal = { month: string; total: number; count: number }
type RecentTxn = CategoryDetailTransaction
type SubCategory = {
  id: string
  name: string
  slug: string
  icon: string | null
  total: number
  count: number
}
type CategoryDetailData = {
  success: true
  category: { id: string; name: string; slug: string; type: string; icon: string | null }
  monthlyTotals: MonthlyTotal[]
  recentTransactions: RecentTxn[]
  subCategories: SubCategory[]
  stats: { total: number; avgMonthly: number; count: number }
}

function CategoryDetail() {
  const { slug } = Route.useParams()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const period = useUIStore((s) => s.period)
  // "All" has months: 0 — map it to all history, as useAnalytics does.
  const months = PERIOD_OPTIONS.find((p) => p.label === period)?.months || 600

  const key = useCryptoStore((s) => s.key)
  const mode = useCryptoStore((s) => s.mode)
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["analytics", "category", slug, months, mode, !!key],
    queryFn: async () => {
      const res = await api.get<CategoryDetailData>(
        `/analytics/categories/${slug}?months=${months}`,
      )
      await decryptTransactionRows(res.recentTransactions, key, mode)
      return res
    },
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return (
      <>
        <Header title="Category" showPeriod action={<BackToAnalytics />} />
        <PageWrapper>
          <DetailSkeleton />
        </PageWrapper>
      </>
    )
  }

  if (isError || !data) {
    return (
      <>
        <Header title="Category" showPeriod action={<BackToAnalytics />} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  const { category, monthlyTotals, recentTransactions, subCategories, stats } = data

  return (
    <>
      <Header
        title={`${category.icon ? `${category.icon} ` : ""}${category.name}`}
        showPeriod
        action={<BackToAnalytics />}
      />
      <PageWrapper>
        <EntityDetailBody
          kind="category"
          name={category.name}
          stats={stats}
          monthlyTotals={monthlyTotals}
          months={months}
          color={CATEGORY_COLORS[category.type] || "var(--text-muted)"}
          isDesktop={isDesktop}
          recent={recentTransactions.map((txn) => ({
            id: txn.id,
            date: txn.date,
            primary: txn.merchantName || txn.description.slice(0, 50),
            amount: txn.amount,
            type: txn.type,
          }))}
          breakdown={
            subCategories.length > 0 && (
              <Card className="min-w-0 gap-0 py-0" data-testid="sub-categories">
                <h2 className="border-b border-border-subtle px-4 py-3 text-callout font-semibold text-text-primary md:px-5">
                  Sub-categories
                </h2>
                <ul>
                  {subCategories.map((sc) => (
                    <li
                      key={sc.id}
                      className="flex min-w-0 items-center justify-between gap-3 border-b border-border-subtle px-4 py-2.5 last:border-0 md:px-5"
                    >
                      <span className="min-w-0 truncate text-body text-text-primary">
                        {sc.icon && <span className="mr-1.5">{sc.icon}</span>}
                        {sc.name}
                      </span>
                      <span className="flex shrink-0 items-baseline gap-2">
                        <Amount value={sc.total} className="text-body" />
                        <span className="font-mono text-caption text-text-muted">{sc.count}×</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            )
          }
        />
      </PageWrapper>
    </>
  )
}
